/**
 * Custom components (owner feedback F20): a typed, versioned definition of a piece of UI that is composed
 * from the design system's primitives and placed anywhere (record and task pages, dashboards, Home, pages,
 * standalone apps in the rail). Pure data, browser-safe, no domain words. The expression-aware analysis
 * (typed contexts, occurrences, the runtime evaluator) lives in `@dataroom/custom-components`.
 *
 *   props     typed inputs the placement supplies (a record, a member, a date, a number …)
 *   state     local, typed values the component keeps while it is open (a selected week, a draft)
 *   data      named, typed bindings onto the data of the workspace (records, one record, an Insight)
 *   actions   named, typed procedures (create / update a record, start a task, run an automation …)
 *   root      the tree of UI nodes (declarative components) — or `code` for the sandboxed escape hatch
 *
 * Every expression (visibility, values, filters, action arguments) is written in the one expression
 * language and checked against a typed context built from this very definition. User-facing texts are
 * `LocalizedText` (en and de by construction). A definition is stored twice: the draft that is edited and
 * the published version that members run.
 */
import { z } from 'zod';
import { FilterNodeSchema } from './filter';
import { type Json, JsonValue } from './json';
import { LocalizedText } from './localized-text';
import { InsightShape, InsightViz } from './doc/insight';

export * from './custom-components-parts';
import { BLOCKS, BLOCK_TYPES, type BlockType, isBlockType } from './custom-blocks-registry';
import { ComponentTheme, NodeStyle } from './custom-style';
import { propShown, walkProps } from './custom-blocks';
import { markupIssueText, markupIssues } from './custom-markup';
export * from './custom-blocks';
export { NOTIFY } from './custom-blocks-kit';
export {
  markupIssues,
  markupIssueText,
  parseMarkup,
  sanitizeStyle,
  scopeCss,
  scopedClass,
  escapeHtml,
  MARKUP_LIMITS,
  MARKUP_TOKENS,
  type MarkupNode,
  type MarkupIssue,
} from './custom-markup';
export * from './custom-style';
export { BLOCKS, BLOCK_TYPES, type BlockType, isBlockType } from './custom-blocks-registry';
import {
  ActionRef,
  ApiKey,
  COMPONENT_DEFINITION_VERSION,
  COMPONENT_LIMITS,
  ChoiceOption,
  Click,
  ComponentKey,
  ExprText,
  Gap,
  NodeId,
  SlotKey,
  Template,
  Text,
  Tone,
  ValueFormat,
  ValueType,
} from './custom-components-parts';

export const PropDef = z
  .object({
    key: SlotKey,
    label: Text(80),
    description: Text(200).optional(),
    type: ValueType,
    required: z.boolean().default(false),
    /** A literal of the prop's type, used when the placement supplies nothing. */
    default: JsonValue.optional(),
  })
  .strict();
export type PropDef = z.infer<typeof PropDef>;

export const StateDef = z
  .object({
    key: SlotKey,
    type: ValueType,
    /** Expression for the starting value (props, viewer and now are available); empty = empty value. */
    initial: ExprText.default(''),
  })
  .strict();
export type StateDef = z.infer<typeof StateDef>;

// -- data ---------------------------------------------------------------------------------------------

/** One sort key of a records source. */
export const SourceSort = z
  .object({ field: z.string().min(1).max(130), dir: z.enum(['asc', 'desc']).default('asc') })
  .strict();

export const STATIC_FIELD_TYPES = ['text', 'number', 'date', 'boolean'] as const;
export const StaticField = z.object({ key: SlotKey, type: z.enum(STATIC_FIELD_TYPES) }).strict();
const StaticCell = z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]);
export const StaticSource = z
  .object({
    id: SlotKey,
    kind: z.literal('static'),
    /** The columns of the rows, with their types (expressions are checked against them). `id` is added when missing. */
    fields: z.array(StaticField).min(1).max(24),
    rows: z.array(z.record(z.string().min(1).max(40), StaticCell)).max(200),
  })
  .strict()
  .superRefine((s, ctx) => {
    const types = new Map<string, string>(s.fields.map((f) => [f.key, f.type]));
    s.rows.forEach((row, i) => {
      for (const [k, v] of Object.entries(row)) {
        const type = types.get(k);
        if (k === 'id') continue;
        if (!type) {
          ctx.addIssue({
            code: 'custom',
            path: ['rows', i, k],
            message: `"${k}" is not a column of this data`,
          });
          continue;
        }
        const ok =
          v === null ||
          (type === 'number'
            ? typeof v === 'number'
            : type === 'boolean'
              ? typeof v === 'boolean'
              : typeof v === 'string');
        if (!ok) ctx.addIssue({ code: 'custom', path: ['rows', i, k], message: `"${k}" must be a ${type}` });
      }
    });
  });
export type StaticSource = z.infer<typeof StaticSource>;

/**
 * Data a component reads, always as the viewing member (record access, field access and row-level rules apply).
 * `filter` is a filter AST whose values may be `{ "$expr": "<expression>" }` (evaluated in the component's context:
 * `state.week_start`, `props.employee`, `viewer.id`, `now` …) next to the usual `{ "$var" }` and `{ "$rel" }` values.
 */
export const DataSource = z.discriminatedUnion('kind', [
  z
    .object({
      id: SlotKey,
      kind: z.literal('records'),
      /** An object key; `workspace_tasks` reads the tasks of the task platform. */
      object: ApiKey,
      filter: FilterNodeSchema.nullable().default(null),
      sort: z.array(SourceSort).max(4).default([]),
      limit: z.number().int().min(1).max(500).default(100),
    })
    .strict(),
  z
    .object({
      id: SlotKey,
      kind: z.literal('record'),
      object: ApiKey,
      /** Expression returning the record id (a record or text value). */
      record: ExprText,
    })
    .strict(),
  z
    .object({
      id: SlotKey,
      kind: z.literal('insight'),
      insight: InsightShape,
      /** Host filter on the insight's object, same value rules as a records source. */
      filter: FilterNodeSchema.nullable().default(null),
    })
    .strict(),
  /** Fixed rows typed by the author: sample data, option lists, lookups. Reads nothing and needs no access. */
  StaticSource,
]);
export type DataSource = z.infer<typeof DataSource>;
export type DataSourceKind = DataSource['kind'];

const StepBase = { when: ExprText.optional() };
/** `values` maps field keys to expressions; each is checked against the field's type. */
const FieldValues = z.record(z.string().min(1).max(63), ExprText).default({});

export const ActionStep = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('set'), state: SlotKey, value: ExprText, ...StepBase }).strict(),
  z.object({ kind: z.literal('reset'), states: z.array(SlotKey).max(24), ...StepBase }).strict(),
  z
    .object({
      kind: z.literal('record.create'),
      /** Names the result for later steps (`steps.<as>.id`). */
      as: SlotKey.optional(),
      object: ApiKey,
      values: FieldValues,
      ...StepBase,
    })
    .strict(),
  z
    .object({
      kind: z.literal('record.update'),
      as: SlotKey.optional(),
      object: ApiKey,
      record: ExprText,
      values: FieldValues,
      ...StepBase,
    })
    .strict(),
  z.object({ kind: z.literal('record.delete'), object: ApiKey, record: ExprText, ...StepBase }).strict(),
  z
    .object({
      kind: z.literal('task.create'),
      as: SlotKey.optional(),
      /** Task type key of the task platform. */
      type: z.string().min(1).max(63),
      title: ExprText,
      description: ExprText.optional(),
      assignee: ExprText.optional(),
      due: ExprText.optional(),
      priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
      /** The record the task is about. */
      link: z.object({ object: ApiKey, record: ExprText }).strict().optional(),
      fields: FieldValues,
      ...StepBase,
    })
    .strict(),
  /**
   * Finish a task with an outcome, as the viewer: the same step the Inbox takes (the task type's playbook runs its next
   * actions, a call task logs the call on the timeline). `outcome` is an expression: write a key as a quoted text,
   * `"connected"`, or read it from an argument, `args.outcome`.
   */
  z
    .object({
      kind: z.literal('task.complete'),
      as: SlotKey.optional(),
      /** Expression returning the task (a task row such as `row`, or its id). */
      task: ExprText,
      outcome: ExprText.optional(),
      /** What happened, written to the task's result (and the call's notes). */
      note: ExprText.optional(),
      /** Cancel the task instead of completing it. */
      cancel: z.boolean().default(false),
      ...StepBase,
    })
    .strict(),
  /** Change fields of a task inline: title, priority, assignee, due, state, status or any field of its type. */
  z
    .object({
      kind: z.literal('task.update'),
      as: SlotKey.optional(),
      task: ExprText,
      /** Field keys of the task data model (`schema.describe workspace_tasks`) to expressions. */
      values: FieldValues,
      ...StepBase,
    })
    .strict(),
  /** Log an engagement (a call with outcome and duration, a note, a meeting …) on a record, as the viewer. */
  z
    .object({
      kind: z.literal('engagement.log'),
      as: SlotKey.optional(),
      /** Engagement type key: call, note, meeting, email or a type of the workspace. */
      type: z.string().min(1).max(63),
      title: ExprText.optional(),
      body: ExprText.optional(),
      /** Expression returning the date and time (default now). */
      at: ExprText.optional(),
      /** Duration in minutes. */
      minutes: ExprText.optional(),
      /** Outcome key of the type, as an expression (`"connected"`). */
      outcome: ExprText.optional(),
      direction: z.enum(['inbound', 'outbound', 'internal']).optional(),
      /** The record the engagement is about (the subject). */
      link: z.object({ object: ApiKey, record: ExprText }).strict(),
      fields: FieldValues,
      ...StepBase,
    })
    .strict(),
  z
    .object({
      kind: z.literal('workflow.run'),
      /** Key or id of an automation that has a manual trigger. */
      workflow: z.string().min(1).max(80),
      link: z.object({ object: ApiKey, record: ExprText }).strict().optional(),
      input: z.record(z.string().min(1).max(63), ExprText).default({}),
      ...StepBase,
    })
    .strict(),
  z
    .object({
      kind: z.literal('navigate'),
      to: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('record'), object: ApiKey, record: ExprText }).strict(),
        /** An in-app path (`/o/people`, `/inbox`). */
        z
          .object({
            kind: z.literal('route'),
            path: z
              .string()
              .regex(/^\/[A-Za-z0-9\-._~/?=&%]*$/)
              .max(300),
          })
          .strict(),
      ]),
      ...StepBase,
    })
    .strict(),
  z
    .object({
      kind: z.literal('message'),
      text: Template,
      tone: z.enum(['success', 'info', 'warning', 'danger']).default('success'),
      ...StepBase,
    })
    .strict(),
  z.object({ kind: z.literal('copilot'), prompt: Template, ...StepBase }).strict(),
]);
export type ActionStep = z.infer<typeof ActionStep>;
export const ACTION_STEP_KINDS = [
  'set',
  'reset',
  'record.create',
  'record.update',
  'record.delete',
  'task.create',
  'task.complete',
  'task.update',
  'engagement.log',
  'workflow.run',
  'navigate',
  'message',
  'copilot',
] as const satisfies ReadonlyArray<ActionStep['kind']>;
/** Steps that run on the server (everything else is a client effect the server returns). */
export const SERVER_STEP_KINDS: ReadonlySet<string> = new Set([
  'record.create',
  'record.update',
  'record.delete',
  'task.create',
  'task.complete',
  'task.update',
  'engagement.log',
  'workflow.run',
]);

export const ActionParam = z.object({ key: SlotKey, label: Text(80).optional(), type: ValueType }).strict();
export type ActionParam = z.infer<typeof ActionParam>;

export const ActionDef = z
  .object({
    id: SlotKey,
    name: Text(80),
    /** Typed arguments of the call (`args.<key>` inside the steps). */
    params: z.array(ActionParam).max(12).default([]),
    steps: z.array(ActionStep).min(1).max(COMPONENT_LIMITS.steps),
    /** Asked before the steps run (destructive actions); other actions offer Undo instead. */
    confirm: Text(200).optional(),
    /** Shown when the steps ran. */
    success: Template.optional(),
  })
  .strict();
export type ActionDef = z.infer<typeof ActionDef>;

// -- nodes --------------------------------------------------------------------------------------------

/** The node types written by hand (before the block library); the blocks of `custom-blocks-*.ts` come after them. */
export const LEGACY_NODE_TYPES = [
  'stack',
  'grid',
  'card',
  'tabs',
  'tab',
  'repeat',
  'heading',
  'text',
  'badge',
  'alert',
  'divider',
  'stat',
  'progress',
  'list',
  'table',
  'calendar',
  'chart',
  'matrix',
  'timer',
  'form',
  'input',
  'button',
] as const;
export type LegacyNodeType = (typeof LEGACY_NODE_TYPES)[number];
export const NODE_TYPES = [...LEGACY_NODE_TYPES, ...BLOCK_TYPES] as unknown as readonly [
  NodeType,
  ...NodeType[],
];
export type NodeType = LegacyNodeType | BlockType;

const Icon = z.string().max(40).optional();
const Variant = z.enum(['primary', 'secondary', 'ghost', 'danger']);

export const TableColumn = z.discriminatedUnion('kind', [
  z
    .object({
      id: SlotKey,
      kind: z.literal('field'),
      header: Text(80).optional(),
      /** A field of the source's object, shown with its typed display. */
      field: z.string().min(1).max(63),
      /** Auto uses the row action when present; record keeps navigation; row shows an inert label. */
      linkBehavior: z.enum(['auto', 'record', 'row', 'none']).default('auto'),
      align: z.enum(['start', 'end']).optional(),
    })
    .strict(),
  z
    .object({
      id: SlotKey,
      kind: z.literal('value'),
      header: Text(80),
      value: ExprText,
      format: ValueFormat.default({ kind: 'auto' }),
      align: z.enum(['start', 'end']).optional(),
    })
    .strict(),
  z
    .object({
      id: SlotKey,
      kind: z.literal('button'),
      header: Text(80).optional(),
      label: Text(40),
      icon: Icon,
      variant: Variant.default('ghost'),
      call: ActionRef,
      visibleWhen: ExprText.optional(),
    })
    .strict(),
]);
export type TableColumn = z.infer<typeof TableColumn>;

const NodeProps = {
  stack: z
    .object({
      direction: z.enum(['column', 'row']).default('column'),
      gap: Gap.default('md'),
      align: z.enum(['start', 'center', 'end', 'stretch', 'baseline']).default('stretch'),
      justify: z.enum(['start', 'center', 'end', 'between']).default('start'),
      wrap: z.boolean().default(false),
    })
    .strict(),
  grid: z.object({ columns: z.number().int().min(1).max(6).default(2), gap: Gap.default('md') }).strict(),
  card: z
    .object({
      title: Text(120).optional(),
      description: Text(300).optional(),
      tone: z.enum(['default', 'subtle', 'accent']).default('default'),
      padded: z.boolean().default(true),
    })
    .strict(),
  tabs: z.object({ bind: SlotKey.optional() }).strict(),
  tab: z.object({ label: Text(60), icon: Icon }).strict(),
  repeat: z
    .object({
      source: SlotKey,
      direction: z.enum(['column', 'row', 'grid']).default('column'),
      columns: z.number().int().min(1).max(6).default(2),
      gap: Gap.default('sm'),
      limit: z.number().int().min(1).max(200).default(50),
      empty: Text(200).optional(),
    })
    .strict(),
  heading: z
    .object({ text: Template, level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2) })
    .strict(),
  text: z
    .object({
      text: Template,
      tone: z.enum(['default', 'muted', 'success', 'warning', 'danger']).default('default'),
      size: z.enum(['sm', 'md', 'lg']).default('md'),
      align: z.enum(['start', 'center', 'end']).default('start'),
    })
    .strict(),
  badge: z.object({ text: Template, tone: Tone.default('neutral'), toneWhen: ExprText.optional() }).strict(),
  alert: z
    .object({
      tone: z.enum(['info', 'success', 'warning', 'danger']).default('info'),
      title: Text(120).optional(),
      text: Template,
    })
    .strict(),
  divider: z.object({}).strict(),
  stat: z
    .object({
      label: Text(80),
      value: ExprText,
      format: ValueFormat.default({ kind: 'auto' }),
      hint: Template.optional(),
      icon: Icon,
      toneWhen: ExprText.optional(),
    })
    .strict(),
  progress: z
    .object({
      label: Text(80).optional(),
      value: ExprText,
      max: ExprText,
      format: ValueFormat.default({ kind: 'auto' }),
      hint: Template.optional(),
      toneWhen: ExprText.optional(),
    })
    .strict(),
  list: z
    .object({
      source: SlotKey,
      primary: Template,
      secondary: Template.optional(),
      meta: Template.optional(),
      badge: z
        .object({ text: Template, tone: Tone.default('neutral'), toneWhen: ExprText.optional() })
        .strict()
        .optional(),
      icon: Icon,
      /** A text that names a person: shown as an avatar with initials. */
      person: ExprText.optional(),
      onClick: Click.optional(),
      empty: Text(200).optional(),
      limit: z.number().int().min(1).max(200).default(20),
      dense: z.boolean().default(false),
    })
    .strict(),
  table: z
    .object({
      source: SlotKey,
      columns: z.array(TableColumn).min(1).max(COMPONENT_LIMITS.columns),
      onRowClick: Click.optional(),
      /** Column id → expression: one footer row (totals). */
      footer: z.record(SlotKey, ExprText).default({}),
      empty: Text(200).optional(),
      limit: z.number().int().min(1).max(200).default(50),
      dense: z.boolean().default(false),
    })
    .strict(),
  calendar: z
    .object({
      source: SlotKey,
      title: ExprText,
      start: ExprText,
      end: ExprText.optional(),
      toneWhen: ExprText.optional(),
      /** A date state key that holds the month on show (so other nodes can follow it); else the calendar keeps its own. */
      month: SlotKey.optional(),
      weekStart: z.enum(['monday', 'sunday']).default('monday'),
      onEventClick: Click.optional(),
      legend: z
        .array(z.object({ tone: Tone, label: Text(40) }).strict())
        .max(5)
        .default([]),
    })
    .strict(),
  chart: z
    .object({
      /** A data source of kind `insight`. */
      source: SlotKey,
      title: Text(120).optional(),
      /** Draw the source as another kind of chart than the one it was written for (bar, line, donut, funnel …). */
      viz: InsightViz.optional(),
      height: z.number().int().min(120).max(560).default(240),
    })
    .strict(),
  matrix: z
    .object({
      /** Records that form the rows (a data source of kind `records`). */
      rows: SlotKey,
      /** Text of a row (`row` is the record). */
      rowLabel: ExprText,
      /** First day shown (expression returning a date) and the number of days. */
      from: ExprText,
      days: z.number().int().min(1).max(14).default(7),
      /** Records that fill the cells (a data source of kind `records`); `entry` is the record. */
      entries: SlotKey,
      entryRow: ExprText,
      entryDate: ExprText,
      entryValue: ExprText,
      unit: Text(20).optional(),
      decimals: z.number().int().min(0).max(2).default(1),
      step: z.number().positive().max(100).default(0.5),
      max: z.number().positive().max(100_000).default(24),
      totals: z.enum(['both', 'rows', 'columns', 'none']).default('both'),
      /** Called when a cell is edited: `cell.row`, `cell.date`, `cell.value`, `cell.entry` are available to the arguments. */
      onChange: ActionRef.optional(),
      empty: Text(200).optional(),
    })
    .strict(),
  timer: z
    .object({
      /** True while a timer runs. */
      running: ExprText,
      /** When the running timer started (date and time), empty when it is stopped. */
      startedAt: ExprText,
      label: Template.optional(),
      startLabel: Text(40),
      stopLabel: Text(40),
      start: ActionRef,
      stop: ActionRef,
      /** Starting is not possible while this holds (a project that has to be chosen first); `startHint` says why. */
      startDisabledWhen: ExprText.optional(),
      startHint: Template.optional(),
      size: z.enum(['md', 'lg']).default('lg'),
    })
    .strict(),
  form: z
    .object({
      submit: z.object({ label: Text(40), icon: Icon, call: ActionRef }).strict(),
      layout: z.enum(['column', 'grid']).default('column'),
      /** The form is valid when this condition holds (empty: when every required input is filled). */
      validWhen: ExprText.optional(),
      invalidMessage: Template.optional(),
      /** States set back to their starting value after the action succeeded. */
      resetStates: z.array(SlotKey).max(24).default([]),
    })
    .strict(),
  input: z
    .object({
      kind: z.enum(['text', 'longtext', 'number', 'date', 'choice', 'record', 'member', 'switch']),
      /** The state key the input edits (its type must fit the kind). */
      bind: SlotKey,
      label: Text(80),
      placeholder: Text(80).optional(),
      help: Text(200).optional(),
      required: z.boolean().default(false),
      suffix: Text(20).optional(),
      disabledWhen: ExprText.optional(),
      /** Earliest and latest date (expressions returning a date). */
      min: ExprText.optional(),
      max: ExprText.optional(),
      /** Fixed options of a choice input … */
      options: z.array(ChoiceOption).max(30).optional(),
      /** … or options taken from a data source. */
      optionsFrom: z.object({ source: SlotKey, value: ExprText, label: ExprText }).strict().optional(),
      /** The object a record input picks from. */
      object: ApiKey.optional(),
    })
    .strict(),
  button: z
    .object({
      label: Text(60),
      icon: Icon,
      variant: Variant.default('secondary'),
      /** Only the icon is drawn (the label stays its name for screen readers and the tooltip): arrows of a week picker. */
      iconOnly: z.boolean().default(false),
      /** What the button runs; a button without an action is drawn disabled and reported as a warning. */
      call: ActionRef.optional(),
      disabledWhen: ExprText.optional(),
    })
    .strict(),
} as const satisfies Record<LegacyNodeType, z.ZodType>;

type BlockSchemas = { [K in BlockType]: (typeof BLOCKS)[K]['schema'] };
const AllNodeProps = {
  ...NodeProps,
  ...(Object.fromEntries(BLOCK_TYPES.map((t) => [t, BLOCKS[t].schema])) as BlockSchemas),
} as typeof NodeProps & BlockSchemas;

export type NodePropsOf<T extends NodeType> = z.infer<(typeof AllNodeProps)[T]>;

/** What a node type may contain. */
const LEGACY_CHILDREN: Record<LegacyNodeType, 'none' | 'many' | 'tabs' | 'inputs'> = {
  stack: 'many',
  grid: 'many',
  card: 'many',
  tabs: 'tabs',
  tab: 'many',
  repeat: 'many',
  heading: 'none',
  text: 'none',
  badge: 'none',
  alert: 'none',
  divider: 'none',
  stat: 'none',
  progress: 'none',
  list: 'none',
  table: 'none',
  calendar: 'none',
  chart: 'none',
  matrix: 'none',
  timer: 'none',
  form: 'many',
  input: 'none',
  button: 'none',
};
export const NODE_CHILDREN: Record<NodeType, 'none' | 'many' | 'tabs' | 'inputs'> = {
  ...LEGACY_CHILDREN,
  ...(Object.fromEntries(
    BLOCK_TYPES.map((t) => [t, (BLOCKS[t] as { children?: unknown }).children ? 'many' : 'none']),
  ) as Record<BlockType, 'none' | 'many'>),
};

/**
 * Whether a node of type `child` may sit directly inside a node of type `parent`. A tab lives only in tabs, a block
 * with `parents` only in those; a container with an `only` list takes nothing else; a form never holds a form.
 */
export function canNest(parent: NodeType, child: NodeType): boolean {
  const childBlock = isBlockType(child) ? BLOCKS[child] : null;
  if (parent === 'tabs') return child === 'tab';
  if (child === 'tab') return false;
  if (childBlock?.parents && !childBlock.parents.includes(parent)) return false;
  if (isBlockType(parent)) {
    const rule = (BLOCKS[parent] as { children?: unknown }).children;
    if (!rule || rule === 'none') return false;
    if (typeof rule === 'object' && 'only' in (rule as object))
      return (rule as { only: readonly string[] }).only.includes(child);
    return true;
  }
  if (!['stack', 'grid', 'card', 'tab', 'repeat', 'form'].includes(parent)) return false;
  if (child === 'form' && parent === 'form') return false;
  return true;
}

/** Nodes that read a record of a source as `row` for their children or templates. */
export const ROW_NODES: ReadonlySet<NodeType> = new Set(['repeat', 'list', 'table', 'calendar']);

export type UiNode = {
  id: string;
  type: NodeType;
  /** Token overrides of this node's look (spacing, corners, colours, type). */
  style?: NodeStyle;
  /** Props of the type (`NODE_PROPS[type]`); parsed with defaults by `nodeProps`. */
  props: Record<string, unknown>;
  /** A condition (True/false) in the node's context; the node is not drawn when it does not hold. */
  visibleWhen?: string;
  children?: UiNode[];
};

export const UiNodeSchema: z.ZodType<UiNode> = z.lazy(() =>
  z
    .object({
      id: NodeId,
      type: z.enum(NODE_TYPES),
      props: z.record(z.string(), z.unknown()).default({}),
      visibleWhen: ExprText.optional(),
      style: NodeStyle.optional(),
      children: z.array(UiNodeSchema).max(60).optional(),
    })
    .strict(),
) as z.ZodType<UiNode>;

/** The schema of a node type's props. */
export function nodePropsSchema<T extends NodeType>(type: T): (typeof AllNodeProps)[T] {
  return AllNodeProps[type];
}
export const NODE_PROPS = AllNodeProps;

/** A node's props parsed with defaults (null when they do not match). */
export function nodeProps<T extends NodeType>(
  node: Pick<UiNode, 'type' | 'props'>,
  type: T,
): NodePropsOf<T> | null {
  if (node.type !== type) return null;
  const r = AllNodeProps[type].safeParse(node.props);
  return r.success ? (r.data as NodePropsOf<T>) : null;
}

const NO_CALL = { action: '', args: {} };
/** What an unfinished node of a type starts from: the required parts empty, so an editor can read every part of it. */
const DRAFT_PROPS: Partial<Record<LegacyNodeType, Record<string, unknown>>> = {
  repeat: { source: '', direction: 'column', columns: 2, gap: 'sm', limit: 50 },
  list: { source: '', primary: '', limit: 20, dense: false },
  table: { source: '', columns: [], footer: {}, limit: 50, dense: false },
  calendar: { source: '', title: '', start: '', weekStart: 'monday', legend: [] },
  chart: { source: '', height: 240 },
  matrix: { rows: '', rowLabel: '', from: '', days: 7, entries: '', entryRow: '', entryDate: '', entryValue: '', decimals: 1, step: 0.5, max: 24, totals: 'both' },
  timer: { running: '', startedAt: '', startLabel: '', stopLabel: '', start: NO_CALL, stop: NO_CALL, size: 'lg' },
  form: { submit: { label: '', call: NO_CALL }, layout: 'column', resetStates: [] },
  input: { kind: 'text', bind: '', label: '', required: false },
  button: { label: '', variant: 'secondary', iconOnly: false },
};

/**
 * A node's props for an editor: parsed with defaults when they are valid, else what they say laid over an empty
 * draft of the type (every list and call present), so a node that is half set up (no data source yet, no action
 * yet, saved by an older build) can still be opened and finished instead of crashing the screen.
 */
export function draftProps<T extends NodeType>(node: Pick<UiNode, 'type' | 'props'>, type: T): NodePropsOf<T> {
  const ok = nodeProps(node, type);
  if (ok) return ok;
  const base = (DRAFT_PROPS as Record<string, Record<string, unknown> | undefined>)[type] ?? {};
  return { ...structuredClone(base), ...node.props } as NodePropsOf<T>;
}

/** A node of a type with default props and a fresh id. */
export function newNode(type: NodeType, props: Record<string, unknown> = {}, id = newNodeId()): UiNode {
  const parsed = AllNodeProps[type].safeParse(props);
  return {
    id,
    type,
    props: parsed.success ? (parsed.data as Record<string, unknown>) : props,
    ...(NODE_CHILDREN[type] === 'none' ? {} : { children: [] }),
  };
}

const ID_CHARS = 'abcdefghijkmnopqrstuvwxyz23456789';
export function newNodeId(): string {
  const bytes = new Uint8Array(6);
  globalThis.crypto.getRandomValues(bytes);
  let out = 'n';
  for (const b of bytes) out += ID_CHARS[b % ID_CHARS.length];
  return out;
}

// -- the definition -----------------------------------------------------------------------------------

/** Hosts of the sandboxed code escape hatch (the SDK reads the same props, data and actions). */
export const CodeSource = z
  .object({
    /** The markup of the component's body (no scripts: the script is `js`). */
    html: z.string().max(COMPONENT_LIMITS.codeBytes).default(''),
    css: z.string().max(COMPONENT_LIMITS.codeBytes).default(''),
    js: z.string().max(COMPONENT_LIMITS.codeBytes).default(''),
    /** Height in px before the component reports its own (`sdk.resize`). */
    height: z.number().int().min(80).max(1600).default(320),
  })
  .strict();
export type CodeSource = z.infer<typeof CodeSource>;

const DefinitionBase = {
  version: z.literal(COMPONENT_DEFINITION_VERSION).default(COMPONENT_DEFINITION_VERSION),
  props: z.array(PropDef).max(COMPONENT_LIMITS.props).default([]),
  state: z.array(StateDef).max(COMPONENT_LIMITS.state).default([]),
  data: z.array(DataSource).max(COMPONENT_LIMITS.data).default([]),
  actions: z.array(ActionDef).max(COMPONENT_LIMITS.actions).default([]),
  /** The look of the whole component (accent, roundness, density, typeface), from the design system's scales. */
  theme: ComponentTheme.optional(),
};

export const DeclarativeDefinition = z
  .object({ ...DefinitionBase, kind: z.literal('declarative').default('declarative'), root: UiNodeSchema })
  .strict();
export const CodeDefinition = z
  .object({ ...DefinitionBase, kind: z.literal('code'), code: CodeSource })
  .strict();
export const ComponentDefinition = z.union([DeclarativeDefinition, CodeDefinition]);
export type ComponentDefinition = z.infer<typeof DeclarativeDefinition> | z.infer<typeof CodeDefinition>;
export type DeclarativeComponentDefinition = z.infer<typeof DeclarativeDefinition>;
export type CodeComponentDefinition = z.infer<typeof CodeDefinition>;
export const isCodeDefinition = (d: ComponentDefinition): d is CodeComponentDefinition => d.kind === 'code';

/** A new component: one vertical stack with a heading. */
export function emptyComponent(): DeclarativeComponentDefinition {
  return {
    version: COMPONENT_DEFINITION_VERSION,
    kind: 'declarative',
    props: [],
    state: [],
    data: [],
    actions: [],
    root: newNode('stack', { direction: 'column', gap: 'md' }, 'root'),
  };
}

// -- where a component is placed ---------------------------------------------------------------------

export const HOME_SIZES = ['sm', 'md', 'lg', 'full'] as const;
export const ComponentPlacement = z
  .object({
    /** Shown on the Home launchpad (props must all have defaults or be optional). */
    home: z
      .object({ enabled: z.boolean().default(false), size: z.enum(HOME_SIZES).default('md') })
      .strict()
      .default({ enabled: false, size: 'md' }),
    /** Opens as its own app: a rail entry under Apps and the page `/apps/<key>`. */
    app: z
      .object({ enabled: z.boolean().default(false) })
      .strict()
      .default({ enabled: false }),
  })
  .strict();
export type ComponentPlacement = z.infer<typeof ComponentPlacement>;

/** What a placement (record or task page, dashboard, Home, page, app) says about a component. */
export const ComponentInput = z.union([
  z.object({ value: JsonValue }).strict(),
  z.object({ expr: ExprText }).strict(),
]);
export type ComponentInput = z.infer<typeof ComponentInput>;
export const ComponentInputs = z.record(SlotKey, ComponentInput).default({});
export type ComponentInputs = z.infer<typeof ComponentInputs>;

/** The reference every placement stores: which component, which inputs. */
export const ComponentRef = z.object({ componentId: z.uuid(), inputs: ComponentInputs }).strict();
export type ComponentRef = z.infer<typeof ComponentRef>;

// -- DTOs ---------------------------------------------------------------------------------------------

export const ComponentOrigin = z.enum(['workspace', 'blueprint', 'agent']);
export type ComponentOrigin = z.infer<typeof ComponentOrigin>;

export const ComponentSummaryDTO = z.object({
  id: z.uuid(),
  key: ComponentKey,
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  kind: z.enum(['declarative', 'code']),
  published: z.boolean(),
  publishedAt: z.string().nullable(),
  hasDraftChanges: z.boolean(),
  placement: ComponentPlacement,
  /** True while the component has props that no placement can leave empty (needs a record, member …). */
  needsInputs: z.boolean(),
  origin: ComponentOrigin,
  updatedAt: z.string(),
  version: z.number().int(),
});
export type ComponentSummaryDTO = z.infer<typeof ComponentSummaryDTO>;

export const ComponentDTO = ComponentSummaryDTO.extend({
  /** What members run (null until published). */
  publishedDefinition: ComponentDefinition.nullable(),
  /** What builders edit (null for members who may not edit). */
  draft: ComponentDefinition.nullable(),
  /** The texts as stored, per language (builders). */
  nameText: LocalizedText,
  descriptionText: LocalizedText,
  canEdit: z.boolean(),
});
export type ComponentDTO = z.infer<typeof ComponentDTO>;

export const CreateComponentInput = z
  .object({
    name: Text(80),
    description: Text(300).optional(),
    key: ComponentKey.optional(),
    icon: z.string().max(40).default('puzzle'),
    definition: ComponentDefinition.optional(),
    /** `blank` or a starter (`timesheet`, `leave`, `stats`) the product ships. */
    starter: z.string().max(40).optional(),
    publish: z.boolean().default(false),
  })
  .strict();
export type CreateComponentInput = z.input<typeof CreateComponentInput>;

export const UpdateComponentInput = z
  .object({
    expectedVersion: z.number().int().positive().optional(),
    name: Text(80).optional(),
    description: Text(300).optional(),
    icon: z.string().max(40).optional(),
    draft: ComponentDefinition.optional(),
    placement: ComponentPlacement.optional(),
  })
  .strict();
export type UpdateComponentInput = z.input<typeof UpdateComponentInput>;

// -- runtime: data and actions -------------------------------------------------------------------------

/** What a data source answered for the viewer: rows are flat (`{ id, title, <field>: value … }`), Insight rows carry label, value, count. */
export const SourceResult = z
  .object({
    rows: z.array(z.record(z.string(), z.unknown())),
    count: z.number().int(),
    /** The first measure's total (Insight sources). */
    value: z.number().nullable().optional(),
    truncated: z.boolean().default(false),
    /** Why the source could not be read (no access, a field that is gone …), in the viewer's language. */
    error: z.string().nullable().optional(),
  })
  .strict();
export type SourceResult = z.infer<typeof SourceResult>;

export const ComponentDataRequest = z
  .object({
    /** Values of the props (a record or member prop as its id). */
    props: z.record(SlotKey, JsonValue).default({}),
    state: z.record(SlotKey, JsonValue).default({}),
    /** Only these sources (default: all). */
    sources: z.array(SlotKey).max(COMPONENT_LIMITS.data).optional(),
    /** Read the draft definition instead of the published one (builders). */
    draft: z.boolean().default(false),
    /** The definition to run, sent by a builder that has not saved yet (builders only). */
    definition: ComponentDefinition.optional(),
    /**
     * `definition` belongs to a document (an element of a slide), not to a builder's draft: any member may read its
     * data sources, as themselves. Declarative definitions only; actions are not served for inline definitions.
     */
    inline: z.boolean().default(false),
  })
  .strict();
export type ComponentDataRequest = z.input<typeof ComponentDataRequest>;

export const ComponentDataResponse = z
  .object({
    /** The viewer as the expressions see them (`viewer.id`, `viewer.name`, custom member fields). */
    viewer: z.record(z.string(), z.unknown()),
    /** Record and member props resolved to their values. */
    props: z.record(z.string(), z.unknown()),
    sources: z.record(z.string(), SourceResult),
    serverNow: z.string(),
  })
  .strict();
export type ComponentDataResponse = z.infer<typeof ComponentDataResponse>;

export const ActionRunRequest = z
  .object({
    props: z.record(SlotKey, JsonValue).default({}),
    state: z.record(SlotKey, JsonValue).default({}),
    args: z.record(SlotKey, JsonValue).default({}),
    /** Evaluate and check every step, change nothing (a builder's "Try it"). */
    dryRun: z.boolean().default(false),
    draft: z.boolean().default(false),
    definition: ComponentDefinition.optional(),
  })
  .strict();
export type ActionRunRequest = z.input<typeof ActionRunRequest>;

/** What the client does after the server ran the steps. */
export const ActionEffect = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('set'), state: SlotKey, value: JsonValue }).strict(),
  z.object({ kind: z.literal('reset'), states: z.array(SlotKey) }).strict(),
  z
    .object({
      kind: z.literal('navigate'),
      to: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('record'), object: z.string(), record: z.string() }).strict(),
        z.object({ kind: z.literal('route'), path: z.string() }).strict(),
      ]),
    })
    .strict(),
  z
    .object({
      kind: z.literal('message'),
      text: z.string(),
      tone: z.enum(['success', 'info', 'warning', 'danger']),
    })
    .strict(),
  z.object({ kind: z.literal('copilot'), prompt: z.string() }).strict(),
]);
export type ActionEffect = z.infer<typeof ActionEffect>;

export const StepReport = z
  .object({
    index: z.number().int(),
    kind: z.string(),
    /** One sentence in the viewer's language ("Created Time entry", "Skipped: condition not met"). */
    summary: z.string(),
    skipped: z.boolean().default(false),
    objectKey: z.string().optional(),
    recordId: z.string().optional(),
    taskId: z.string().optional(),
    runId: z.string().optional(),
  })
  .strict();
export type StepReport = z.infer<typeof StepReport>;

export const ActionRunResponse = z
  .object({
    effects: z.array(ActionEffect),
    steps: z.array(StepReport),
    /** Reverts the records the steps wrote (record-ops undo token). */
    undo: z.unknown().nullable(),
    dryRun: z.boolean(),
    /** Shown as a toast when the action has a success text. */
    message: z.string().nullable(),
  })
  .strict();
export type ActionRunResponse = z.infer<typeof ActionRunResponse>;

// -- problems (references inside a definition) -------------------------------------------------------

export type DefinitionProblem = {
  /** Where: `data.entries`, `root.n3x.props.source`, `actions.start.steps.0`. */
  path: string;
  code:
    | 'duplicate_key'
    | 'unknown_source'
    | 'unknown_state'
    | 'unknown_action'
    | 'unknown_param'
    | 'source_kind'
    | 'bad_children'
    | 'too_many_nodes'
    | 'too_deep'
    | 'duplicate_node'
    | 'tab_parent'
    | 'input_kind'
    | 'missing_object'
    | 'no_root'
    | 'missing_args'
    | 'missing_source'
    | 'missing_state'
    | 'missing_action'
    | 'bad_parent'
    | 'state_kind'
    | 'bad_props'
    | 'markup';
  params: Record<string, string | number>;
};

/** Every reference of a definition that does not resolve (data sources, state, actions, structure). Pure. */
export function definitionProblems(def: ComponentDefinition): DefinitionProblem[] {
  const out: DefinitionProblem[] = [];
  const add = (path: string, code: DefinitionProblem['code'], params: DefinitionProblem['params'] = {}) =>
    out.push({ path, code, params });
  const dup = (list: Array<{ key?: string; id?: string }>, at: string) => {
    const seen = new Set<string>();
    for (const [i, x] of list.entries()) {
      const k = x.key ?? x.id ?? '';
      if (seen.has(k)) add(`${at}.${i}`, 'duplicate_key', { key: k });
      seen.add(k);
    }
  };
  dup(def.props, 'props');
  dup(def.state, 'state');
  dup(def.data, 'data');
  dup(def.actions, 'actions');
  const states = new Map(def.state.map((s) => [s.key, s]));
  const sources = new Map(def.data.map((s) => [s.id, s]));
  const actions = new Map(def.actions.map((a) => [a.id, a]));
  for (const k of def.props.map((p) => p.key))
    if (states.has(k)) add(`state.${k}`, 'duplicate_key', { key: k });

  const callProblems = (path: string, call: ActionRef) => {
    const a = actions.get(call.action);
    if (!a) return add(`${path}.action`, 'unknown_action', { action: call.action });
    for (const p of a.params)
      if (!(p.key in call.args)) add(`${path}.args`, 'missing_args', { action: a.id, param: p.key });
    for (const k of Object.keys(call.args))
      if (!a.params.some((p) => p.key === k))
        add(`${path}.args.${k}`, 'unknown_param', { action: a.id, param: k });
  };
  const clickProblems = (path: string, click: Click | undefined) => {
    if (click?.kind === 'action') callProblems(`${path}.call`, click.call);
  };
  const need = (path: string, id: string | undefined, kinds: DataSourceKind[]) => {
    if (!id) return;
    const s = sources.get(id);
    if (!s) return add(path, 'unknown_source', { source: id });
    if (!kinds.includes(s.kind)) add(path, 'source_kind', { source: id, kind: s.kind });
  };
  const needState = (path: string, key: string | undefined) => {
    if (key && !states.has(key)) add(path, 'unknown_state', { state: key });
  };

  for (const [ai, a] of def.actions.entries()) {
    dup(a.params, `actions.${ai}.params`);
    const results = new Set<string>();
    for (const [si, s] of a.steps.entries()) {
      const at = `actions.${ai}.steps.${si}`;
      if (s.kind === 'set') needState(`${at}.state`, s.state);
      if (s.kind === 'reset') for (const k of s.states) needState(`${at}.states`, k);
      if ('as' in s && s.as) {
        if (results.has(s.as)) add(`${at}.as`, 'duplicate_key', { key: s.as });
        results.add(s.as);
      }
    }
  }
  for (const s of def.data) {
    if (s.kind === 'insight' || s.kind === 'static') continue;
    if (!s.object) add(`data.${s.id}`, 'missing_object');
  }

  if (def.kind === 'code') return out;

  /** References of a block's props, found through the declaration of the block (sources, states, calls, clicks). */
  const blockProblems = (n: UiNode, at: string) => {
    if (!isBlockType(n.type)) return;
    const block = BLOCKS[n.type];
    const parsed = block.schema.safeParse(n.props);
    if (!parsed.success) {
      add(at, 'bad_props', { type: n.type, detail: parsed.error.issues[0]?.message ?? '' });
      return;
    }
    if (n.type === 'markup') {
      const mp = parsed.data as { html?: unknown; css?: string };
      const texts =
        typeof mp.html === 'string'
          ? [mp.html]
          : mp.html && typeof mp.html === 'object'
            ? Object.values(mp.html as Record<string, string>)
            : [];
      const seen = new Set<string>();
      for (const html of texts.length ? texts : ['']) {
        for (const i of markupIssues(
          html,
          texts.indexOf(html) === 0 || !texts.length ? (mp.css ?? '') : '',
        )) {
          if (seen.has(i.message)) continue;
          seen.add(i.message);
          add(`${at}.props.${i.code.startsWith('css') ? 'css' : 'html'}`, 'markup', { msg: i.message });
        }
      }
    }
    walkProps(block.props, parsed.data as Record<string, unknown>, ({ path, prop, value }) => {
      const meta = prop.meta;
      const here = `${at}.${path}`;
      if (meta.k === 'source') {
        if (typeof value === 'string' && value) need(here, value, meta.kinds as DataSourceKind[]);
        else if (!meta.opt && !(prop.when && !propShown(prop, parsed.data as Record<string, unknown>)))
          add(here, 'missing_source');
      } else if (meta.k === 'state') {
        if (typeof value === 'string' && value) {
          needState(here, value);
          const st = states.get(value);
          if (st && meta.types.length && !meta.types.includes(st.type.kind))
            add(here, 'state_kind', { state: value, kind: st.type.kind });
        } else if (!(
          prop.zod.safeParse(undefined).success && prop.zod.safeParse(undefined).data === undefined
        ))
          add(here, 'missing_state');
      } else if (meta.k === 'call') {
        if (value && typeof value === 'object') callProblems(here, value as ActionRef);
        else if (meta.req) add(here, 'missing_action');
      } else if (meta.k === 'click') {
        clickProblems(here, value as Click | undefined);
      }
    });
  };

  /** A hand-written node whose props do not parse is half set up: say what is missing (a source, an action, a state). */
  const setupProblem = (n: UiNode, at: string) => {
    const type = n.type as LegacyNodeType;
    const r = AllNodeProps[type].safeParse(n.props);
    if (r.success) return;
    // What is missing, most telling first: a data source, then an action, then the state an input edits.
    const issues = r.error.issues;
    const head = (i: (typeof issues)[number]) => String(i.path[0] ?? '');
    const source = issues.find((i) => ['source', 'rows', 'entries'].includes(head(i)));
    const call = issues.find((i) => ['start', 'stop', 'call'].includes(head(i)) || (head(i) === 'submit' && i.path[1] === 'call'));
    const bind = type === 'input' ? issues.find((i) => head(i) === 'bind') : undefined;
    if (source) add(`${at}.${head(source)}`, 'missing_source');
    else if (call) add(`${at}.${head(call)}`, 'missing_action');
    else if (bind) add(`${at}.bind`, 'missing_state');
    else {
      const issue = issues[0];
      const last = String(issue?.path[issue.path.length - 1] ?? '');
      add(`${at}.${head(issue!)}`, 'bad_props', { type, detail: last ? `${last}: ${issue?.message ?? ''}` : (issue?.message ?? '') });
    }
  };

  let nodes = 0;
  const ids = new Set<string>();
  const visit = (n: UiNode, path: string, depth: number, parent: UiNode | null) => {
    nodes++;
    if (nodes === COMPONENT_LIMITS.nodes + 1) add(path, 'too_many_nodes', { max: COMPONENT_LIMITS.nodes });
    if (depth > COMPONENT_LIMITS.depth) add(path, 'too_deep', { max: COMPONENT_LIMITS.depth });
    if (ids.has(n.id)) add(path, 'duplicate_node', { id: n.id });
    ids.add(n.id);
    const rule = NODE_CHILDREN[n.type];
    if (rule === 'none' && n.children?.length) add(path, 'bad_children', { type: n.type });
    if (n.type === 'tab' && parent?.type !== 'tabs') add(path, 'tab_parent');
    else if (parent && n.type !== 'tab' && !canNest(parent.type, n.type))
      add(path, 'bad_parent', { type: n.type, parent: parent.type });
    if (n.type === 'tabs')
      for (const c of n.children ?? []) if (c.type !== 'tab') add(path, 'bad_children', { type: n.type });
    const p = n.props;
    const at = `${path}.props`;
    switch (n.type) {
      case 'repeat': {
        const v = nodeProps(n, 'repeat');
        if (!v) setupProblem(n, at);
        if (v) need(`${at}.source`, v.source, ['records']);
        break;
      }
      case 'list': {
        const v = nodeProps(n, 'list');
        if (!v) setupProblem(n, at);
        if (v) {
          need(`${at}.source`, v.source, ['records']);
          clickProblems(`${at}.onClick`, v.onClick);
        }
        break;
      }
      case 'table': {
        const v = nodeProps(n, 'table');
        if (!v) setupProblem(n, at);
        if (v) {
          need(`${at}.source`, v.source, ['records']);
          clickProblems(`${at}.onRowClick`, v.onRowClick);
          v.columns.forEach((c, i) => {
            if (c.kind === 'button') callProblems(`${at}.columns.${i}.call`, c.call);
          });
          const ids2 = new Set(v.columns.map((c) => c.id));
          for (const k of Object.keys(v.footer))
            if (!ids2.has(k)) add(`${at}.footer.${k}`, 'unknown_param', { param: k });
          dup(v.columns, `${at}.columns`);
        }
        break;
      }
      case 'calendar': {
        const v = nodeProps(n, 'calendar');
        if (!v) setupProblem(n, at);
        if (v) {
          need(`${at}.source`, v.source, ['records']);
          needState(`${at}.month`, v.month);
          clickProblems(`${at}.onEventClick`, v.onEventClick);
        }
        break;
      }
      case 'chart': {
        const v = nodeProps(n, 'chart');
        if (!v) setupProblem(n, at);
        if (v) need(`${at}.source`, v.source, ['insight']);
        break;
      }
      case 'matrix': {
        const v = nodeProps(n, 'matrix');
        if (!v) setupProblem(n, at);
        if (v) {
          need(`${at}.rows`, v.rows, ['records']);
          need(`${at}.entries`, v.entries, ['records']);
          if (v.onChange) callProblems(`${at}.onChange`, v.onChange);
        }
        break;
      }
      case 'timer': {
        const v = nodeProps(n, 'timer');
        if (!v) setupProblem(n, at);
        if (v) {
          callProblems(`${at}.start`, v.start);
          callProblems(`${at}.stop`, v.stop);
        }
        break;
      }
      case 'form': {
        const v = nodeProps(n, 'form');
        if (!v) setupProblem(n, at);
        if (v) {
          callProblems(`${at}.submit.call`, v.submit.call);
          for (const k of v.resetStates) needState(`${at}.resetStates`, k);
        }
        break;
      }
      case 'input': {
        const v = nodeProps(n, 'input');
        if (!v) setupProblem(n, at);
        if (v) {
          needState(`${at}.bind`, v.bind);
          const st = states.get(v.bind);
          if (st && !inputFits(v.kind, st.type))
            add(`${at}.bind`, 'input_kind', { state: v.bind, kind: v.kind });
          if (v.optionsFrom) need(`${at}.optionsFrom.source`, v.optionsFrom.source, ['records']);
          if (v.kind === 'record' && !v.object && st?.type.kind !== 'record')
            add(`${at}.object`, 'missing_object');
        }
        break;
      }
      case 'button': {
        const v = nodeProps(n, 'button');
        if (v?.call) callProblems(`${at}.call`, v.call);
        break;
      }
      case 'tabs': {
        const v = nodeProps(n, 'tabs');
        if (v) needState(`${at}.bind`, v.bind);
        break;
      }
      default:
        if (isBlockType(n.type)) blockProblems(n, at);
        else void p;
    }
    n.children?.forEach((c, i) => visit(c, `${path}.children.${i}`, depth + 1, n));
  };
  visit(def.root, 'root', 1, null);
  return out;
}

/** Whether an input kind can edit a state of this type. */
export function inputFits(kind: NodePropsOf<'input'>['kind'], type: ValueType): boolean {
  switch (kind) {
    case 'text':
    case 'longtext':
      return type.kind === 'text';
    case 'number':
      return type.kind === 'number';
    case 'date':
      return type.kind === 'date';
    case 'choice':
      return type.kind === 'choice' || type.kind === 'text';
    case 'record':
      return type.kind === 'record';
    case 'member':
      return type.kind === 'member';
    case 'switch':
      return type.kind === 'boolean';
  }
}

/** Visit every node (depth first, parents before children). */
export function walkNodes(
  root: UiNode,
  visit: (node: UiNode, parent: UiNode | null, path: string) => void,
): void {
  const go = (n: UiNode, parent: UiNode | null, path: string) => {
    visit(n, parent, path);
    n.children?.forEach((c, i) => go(c, n, `${path}.children.${i}`));
  };
  go(root, null, 'root');
}

/** Find a node by id. */
export function findNode(
  root: UiNode,
  id: string,
): { node: UiNode; parent: UiNode | null; path: string } | null {
  let hit: { node: UiNode; parent: UiNode | null; path: string } | null = null;
  walkNodes(root, (node, parent, path) => {
    if (!hit && node.id === id) hit = { node, parent, path };
  });
  return hit;
}

/** `true` when a literal fits a typed value (used for defaults and `{ value }` inputs). */
export function literalFits(type: ValueType, value: Json): boolean {
  if (value === null) return true;
  switch (type.kind) {
    case 'text':
      return typeof value === 'string';
    case 'number':
      return (
        typeof value === 'number' &&
        (type.min === undefined || value >= type.min) &&
        (type.max === undefined || value <= type.max)
      );
    case 'boolean':
      return typeof value === 'boolean';
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    case 'choice':
      return typeof value === 'string' && type.options.some((o) => o.key === value);
    case 'record':
    case 'member':
      return typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value);
    case 'list':
      return Array.isArray(value) && value.every((x) => typeof x === 'string');
  }
}

/** Props that a placement cannot leave empty. */
export function requiredProps(def: ComponentDefinition): PropDef[] {
  return def.props.filter((p) => p.required && p.default === undefined);
}

/**
 * The wording of a schema check (zod) in German: the detail of `bad_props` comes from the schema of a block, in
 * English, and a German message must not carry an English fragment. Unknown details are kept as they are.
 */
const DETAIL_DE: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^Use lower_snake_case starting with a letter$/, () => 'Nur Kleinbuchstaben, Ziffern und Unterstriche (snake_case), beginnend mit einem Buchstaben'],
  [/^Use lower-case letters, digits, "-" and "_", starting with a letter$/, () => 'Nur Kleinbuchstaben, Ziffern, „-“ und „_“, beginnend mit einem Buchstaben'],
  [/^Invalid input: expected (\w+), received (\w+)$/, (m) => `Ungültige Eingabe: erwartet ${m[1]}, erhalten ${m[2]}`],
  [/^Invalid input$/, () => 'Ungültige Eingabe'],
  [/^Invalid option: expected one of (.+)$/, (m) => `Ungültige Option: erlaubt sind ${m[1]}`],
  [/^Too small: expected (\w+) to have >=?\s?(\d+) (\w+)$/, (m) => `Zu klein: mindestens ${m[2]} erwartet`],
  [/^Too small: expected (\w+) to be >=?\s?(-?[\d.]+)$/, (m) => `Zu klein: mindestens ${m[2]} erwartet`],
  [/^Too big: expected (\w+) to have <=?\s?(\d+) (\w+)$/, (m) => `Zu groß: höchstens ${m[2]} erwartet`],
  [/^Too big: expected (\w+) to be <=?\s?(-?[\d.]+)$/, (m) => `Zu groß: höchstens ${m[2]} erwartet`],
];
export function detailText(detail: string, locale: string): string {
  if (!locale.startsWith('de')) return detail;
  for (const [re, fn] of DETAIL_DE) {
    const m = detail.match(re);
    if (m) return fn(m);
  }
  // "label: <message>" (a hand-written node names the prop first).
  const named = detail.match(/^([A-Za-z0-9_.]+): (.+)$/);
  return named ? `${named[1]}: ${detailText(named[2]!, locale)}` : detail;
}

/** Messages of the definition problems, in a language (the server and the builder show the same words). */
export function problemMessage(p: DefinitionProblem, locale: string): string {
  const de = locale.startsWith('de');
  const x = p.params;
  switch (p.code) {
    case 'duplicate_key':
      return de ? `„${x.key}“ kommt mehrfach vor` : `"${x.key}" is used twice`;
    case 'unknown_source':
      return de
        ? `Die Datenquelle „${x.source}“ gibt es nicht`
        : `The data source "${x.source}" does not exist`;
    case 'unknown_state':
      return de ? `Der Zustand „${x.state}“ gibt es nicht` : `The state "${x.state}" does not exist`;
    case 'unknown_action':
      return de ? `Die Aktion „${x.action}“ gibt es nicht` : `The action "${x.action}" does not exist`;
    case 'unknown_param':
      return de
        ? `„${x.param}“ gehört nicht zu dieser Aktion oder Spalte`
        : `"${x.param}" does not belong to this action or column`;
    case 'missing_args':
      return de
        ? `Der Aufruf von „${x.action}“ braucht einen Wert für „${x.param}“`
        : `The call of "${x.action}" needs a value for "${x.param}"`;
    case 'source_kind':
      return de
        ? `Die Datenquelle „${x.source}“ ist vom Typ „${x.kind}“ und passt hier nicht`
        : `The data source "${x.source}" is of kind "${x.kind}" and does not fit here`;
    case 'bad_children':
      return de
        ? `Ein Element vom Typ „${x.type}“ darf diese Unterelemente nicht haben`
        : `A "${x.type}" node cannot have these children`;
    case 'too_many_nodes':
      return de ? `Höchstens ${x.max} Elemente` : `At most ${x.max} nodes`;
    case 'too_deep':
      return de ? `Höchstens ${x.max} Ebenen tief` : `At most ${x.max} levels deep`;
    case 'duplicate_node':
      return de ? `Die Element-ID „${x.id}“ kommt mehrfach vor` : `The node id "${x.id}" is used twice`;
    case 'tab_parent':
      return de ? 'Ein Tab gehört in ein Tabs-Element' : 'A tab belongs inside a tabs node';
    case 'input_kind':
      return de
        ? `Das Eingabefeld passt nicht zum Typ des Zustands „${x.state}“`
        : `The input does not fit the type of the state "${x.state}"`;
    case 'missing_object':
      return de ? 'Wählen Sie ein Objekt' : 'Choose an object';
    case 'no_root':
      return de ? 'Die Komponente hat kein Hauptelement' : 'The component has no root node';
    case 'markup':
      return markupIssueText({ message: String(x.msg) }, locale);
    case 'missing_source':
      return de ? 'Wählen Sie eine Datenquelle' : 'Choose a data source';
    case 'missing_state':
      return de
        ? 'Wählen Sie den Zustand, den dieses Element bearbeitet'
        : 'Choose the state this element edits';
    case 'missing_action':
      return de ? 'Wählen Sie eine Aktion' : 'Choose an action';
    case 'bad_parent':
      return de
        ? `Ein Element vom Typ „${x.type}“ gehört nicht in „${x.parent}“`
        : `A "${x.type}" node does not belong inside "${x.parent}"`;
    case 'state_kind':
      return de
        ? `Der Zustand „${x.state}“ (${x.kind}) passt nicht zu diesem Element`
        : `The state "${x.state}" (${x.kind}) does not fit this element`;
    case 'bad_props':
      return de
        ? `Die Eigenschaften von „${x.type}“ sind ungültig: ${detailText(String(x.detail ?? ''), locale)}`
        : `The settings of "${x.type}" are not valid: ${x.detail}`;
  }
}
